// components/transferts/TransfertList.tsx
import React, { useState } from "react";
import TransfertItem from "./TransfertItem";
import { RefreshCw, AlertCircle, Filter, Clock, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { BankTransfertItem } from "../../../../types/features/administration/VirementBancaire";

interface TransfertListProps {
  transferts: BankTransfertItem[];
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  onViewDetails: (item: BankTransfertItem) => void;
  onCheckStatus: (item: BankTransfertItem) => void;
  onChangeStatus: (item: BankTransfertItem) => void;
  onVerifyKey: (item: BankTransfertItem) => void;
  onComplete: (item: BankTransfertItem) => void;
  onInProgress: (item: BankTransfertItem) => void;
  onFail: (item: BankTransfertItem) => void;
  onCancel: (item: BankTransfertItem) => void;
  loadingStates?: {
    complete?: string;
    inProgress?: string;
    fail?: string;
    cancel?: string;
  };
}

export default function TransfertList({
  transferts,
  loading = false,
  error = null,
  onRefresh,
  onViewDetails,
  onCheckStatus,
  onChangeStatus,
  onVerifyKey,
  onComplete,
  onInProgress,
  onFail,
  onCancel,
  loadingStates = {},
}: TransfertListProps) {
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getFilteredTransferts = () => {
    if (filterStatus === "all") return transferts;
    if (filterStatus === "manual_payout") return transferts.filter((t) => t.details.status === "PENDING_MANUAL_PAYOUT");
    return transferts.filter((t) => t.transfert.status === filterStatus);
  };

  const filteredTransferts = getFilteredTransferts();
  const totalPages = Math.ceil(filteredTransferts.length / itemsPerPage);
  const paginatedTransferts = filteredTransferts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const stats = {
    total: transferts.length,
    pending: transferts.filter((t) => t.transfert.status === "PENDING").length,
    completed: transferts.filter((t) => t.transfert.status === "SUCCEEDED").length,
    failed: transferts.filter((t) => t.transfert.status === "FAILED").length,
    manualPayout: transferts.filter((t) => t.details.status === "PENDING_MANUAL_PAYOUT").length,
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
        <p className="text-red-600 font-semibold">{error}</p>
        {onRefresh && (
          <button onClick={onRefresh} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg">
            Réessayer
          </button>
        )}
      </div>
    );
  }

  if (transferts.length === 0) {
    return <div className="bg-white rounded-lg p-12 text-center text-gray-500">Aucun transfert trouvé</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg p-4 sticky top-0 z-10 border-b">
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => {
              setFilterStatus("all");
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-sm ${filterStatus === "all" ? "bg-blue-600 text-white" : "bg-gray-100"}`}
          >
            Tous ({stats.total})
          </button>
          <button
            onClick={() => {
              setFilterStatus("PENDING");
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-sm ${filterStatus === "PENDING" ? "bg-blue-600 text-white" : "bg-gray-100"}`}
          >
            <Clock className="w-3 h-3 inline mr-1" />
            En attente ({stats.pending})
          </button>
          <button
            onClick={() => {
              setFilterStatus("SUCCEEDED");
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-sm ${filterStatus === "SUCCEEDED" ? "bg-green-600 text-white" : "bg-gray-100"}`}
          >
            <CheckCircle className="w-3 h-3 inline mr-1" />
            Complétés ({stats.completed})
          </button>
          <button
            onClick={() => {
              setFilterStatus("FAILED");
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-sm ${filterStatus === "FAILED" ? "bg-red-600 text-white" : "bg-gray-100"}`}
          >
            Échoués ({stats.failed})
          </button>
          <button
            onClick={() => {
              setFilterStatus("manual_payout");
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-sm ${filterStatus === "manual_payout" ? "bg-orange-600 text-white" : "bg-gray-100"}`}
          >
            Paiement manuel ({stats.manualPayout})
          </button>
          {onRefresh && (
            <button onClick={onRefresh} className="ml-auto px-3 py-1.5 bg-gray-100 rounded-lg">
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {paginatedTransferts.map((item) => (
          <TransfertItem
            key={item.transfert.id}
            transfertItem={item}
            onViewDetails={onViewDetails}
            onCheckStatus={onCheckStatus}
            onChangeStatus={onChangeStatus}
            onVerifyKey={onVerifyKey}
            onComplete={onComplete}
            onInProgress={onInProgress}
            onFail={onFail}
            onCancel={onCancel}
            loadingStates={loadingStates}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 py-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 border rounded disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-4 py-2">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 border rounded disabled:opacity-50"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
